<template>
  <auth-layout class="authCode" :show-agreement="false" :show-support="!isVerify">
    <template #preHeader>
      <mq-responsive target="mobile">
        <div class="authCode_preHeader">
          <div class="authCode_close" @click="handleClose">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_20785_78407)">
              <path d="M8 8L20 20M20 8L8 20" stroke="#203454" stroke-width="1.5" stroke-linecap="round"/>
              </g>
              <defs>
              <clipPath id="clip0_20785_78407">
              <rect width="28" height="28" fill="white"/>
              </clipPath>
              </defs>
            </svg>
          </div>
        </div>
      </mq-responsive>
    </template>
    <template #title>
      <div class="authCode_title">{{ titleText }}</div>
    </template>
    <template #description>
      <div class="authCode_description" v-html="descriptionText"/>
      <div class="authCode_phone">{{ formattedMaskedDestinationPhone }} <span @click="handleChangePhone" class="authCode_phone_change">{{ $t('change') }}</span></div>
    </template>
    <template #content>
      <div class="authCode_content">
        <form class="authCode_form" @submit.prevent>
          <div class="authCode_input">
            <InputV3
              v-model="code"
              placeholder="0000"
              :label="$t('auth.code.code')"
              type="number"
              :maxlength="4"
              @input="handleContinue"
            />
            <div class="authCode_input_error" v-if="error">{{ error }}</div>
          </div>
          <div v-if="isShowNoAccessEmail" class="authCode_noAccessEmail">
            <div>{{ $t('auth.code.noAccessEmail') }}</div>
            <div class="authCode_noAccessEmail_link" @click="handleSendCodeBySMS">{{ $t('auth.code.getCodeBySms') }}</div>
          </div>
          <div>
            <ac-button
              class="authCode_button-continue"
              type="button"
              variant="tonal"
              block
              :disabled="isDisabled"
              @click="handleSendAgain"
            >
              {{ $t('auth.code.getNewCode') }} <span class="authCode_button-continue_timer" v-if="isTimerActive">{{ formatTimerSeconds }}</span>
            </ac-button>
          </div>
        </form>

        <template v-if="!isVerify">
          <auth-enter-with />

          <ac-button
            variant="text"
            color="gray"
            class="authCode_enter-phone"
            block
            @click="handleEnterEmail"
          >
            {{ $t('auth.enterByEmail') }}
          </ac-button>
        </template>
      </div>
    </template>
  </auth-layout>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from '@web/store'
import { MqResponsive } from 'vue3-mq'
import i18n from '@web/plugins/i18n'

import AuthLayout from '@web/platform.v3/layouts/auth.vue'
import InputV3 from '@web/platform.v3/components/input.vue'
import AcButton from '@ui-kit/components/AcButton.vue'
import AuthEnterWith from '@web/platform.v3/components/auth/enter-with.vue'
import PAGE_NAME from '@web/consts/PAGE_NAME'
import { useAuthCatchErrors } from '@web/platform.v3/composables/useAuthCatchErrors'
import useRedirectAfterLogin from '@web/platform.v3/composables/useRedirectAfterLogin'

defineOptions({
  name: 'AuthPhoneCode'
})

const props = defineProps({
  isVerify: {
    type: Boolean,
    default: false
  }
})

const { redirectAfterLogin } = useRedirectAfterLogin()

const store = useStore()
const router = useRouter()

const code = ref('')
const error = ref('')
const isLoading = ref(false)
const user = computed(() => store.getters['user/user'])
const maskedDestinationEmail = computed(() => store.getters['authV3/maskedDestinationEmail'])
const maskedDestinationPhone = computed(() => store.getters['authV3/maskedDestinationPhone'])
const storedPhone = computed(() => store.getters['authV3/phone'])
const isTimerActive = computed(() => store.getters['authV3/isTimerActive'])
const isSendCodeBySMS = computed(() => props.isVerify || store.getters['authV3/isSendCodeBySMS'])
const needStartTimer = computed(() => store.getters['authV3/needStartTimer'])
const timerSeconds = computed(() => store.getters['authV3/timerSeconds'])
const formatTimerSeconds = computed(() => {
  return `00:${timerSeconds.value.toString().padStart(2, '0')}`
})
const authOptions = computed(() => store.getters['authV3/getAuthOptions'])
const isSmsAvailable = computed(() => authOptions.value?.smsAvailable)
const isShowNoAccessEmail = computed(() => isSmsAvailable.value && !isSendCodeBySMS.value)
const titleText = computed(() => {
  return isSendCodeBySMS.value ? i18n.global.t('auth.code.titlePhone') : i18n.global.t('auth.code.titleEmail')
})
const descriptionText = computed(() => {
  return isSendCodeBySMS.value ? i18n.global.t('auth.code.descriptionPhoneSms') : i18n.global.t('auth.code.descriptionPhone', { email: maskedDestinationEmail.value })
})
const isDisabled = computed(() => isTimerActive.value)
const formattedMaskedDestinationPhone = computed(() => props.isVerify ? storedPhone.value : maskedDestinationPhone.value)

const { catchError } = useAuthCatchErrors()

const handleContinue = async () => {
  if (code.value.length !== 4) return
  if (isLoading.value) return
  isLoading.value = true
  try {
    if (props.isVerify) {
      await store.dispatch('authV3/verifyPhone', { phone: storedPhone.value, code: code.value })
    } else {
      await store.dispatch('authV3/verifyPhoneCode', { phone: storedPhone.value, code: code.value, useSms: isSendCodeBySMS.value })
    }
    store.commit('authV3/reset')
    redirectAfterLogin(user.value)
  } catch (e) {
    error.value = catchError(e)
  } finally {
    isLoading.value = false
  }
}

async function handleSendCodeBySMS () {
  try {
    await store.dispatch('authV3/requestPhoneCode', { phone: storedPhone.value, useSms: true })
    store.commit('authV3/setIsSendCodeBySMS', true)
  } catch (e) {
    error.value = catchError(e)
  }
}
async function handleSendAgain () {
  try {
    if (props.isVerify) {
      await store.dispatch('authV3/requestPhoneCodeForVerify', storedPhone.value)
    } else {
      await store.dispatch('authV3/requestPhoneCode', { phone: storedPhone.value, useSms: isSendCodeBySMS.value })
    }
    store.dispatch('authV3/startTimer', 60)
  } catch (e) {
    error.value = catchError(e)
  }
}
function handleEnterEmail () {
  handleChangePhone()
  router.replace({ name: 'login', params: { type: 'email' } })
}

function handleClose () {
  handleChangePhone()
}

function handleChangePhone () {
  code.value = ''
  error.value = ''
  store.commit('authV3/setNeedStartTimer', false)
  store.commit('authV3/setPhoneStatus', 'phone')
}

watch(needStartTimer, (newVal) => {
  if (newVal) {
    store.commit('authV3/setNeedStartTimer', false)
    store.dispatch('authV3/startTimer', 60)
  }
}, {
  immediate: true,
  once: true
})
</script>

<style lang="scss" scoped>
  @import '@web/platform.v3/styles/auth/code.scss';
</style>
